import type { SiteData } from '../../shared/types/site.js';
import { defaultSiteData } from '../data/defaults.js';
import { getSector } from '../sector.js';
import { t } from '../i18n.js';
import { renderPageCta, renderPageHero } from './shared.js';
import { $, escapeHtml } from '../utils/index.js';

function toAnchor(value: string): string {
  return value
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function renderTechItem(name: string | undefined, logoUrl: string | undefined): string {
  return `
    <span class="tech-chip${logoUrl ? ' tech-chip-with-icon' : ''}">
      ${logoUrl ? `<span class="tech-chip-icon"><img src="${logoUrl}" alt="" loading="lazy" onerror="this.parentElement.style.display='none'" /></span>` : ''}
      <span>${escapeHtml(name ?? '')}</span>
    </span>
  `;
}

export function renderTechnologies(siteData: SiteData): void {
  const el = $('#page-technologies');
  if (!el) return;

  const sector = getSector();
  const tech = siteData.technologies ?? defaultSiteData.technologies!;
  const categories = tech.categories ?? [];
  const preferredOrder = ['Mobile', 'Frontend', 'Backend', 'Cloud', 'Databases', 'Product', 'DevOps', 'Game Development'];
  const activeCategories = categories
    .filter((category) => (category.items ?? []).length > 0)
    .sort((a, b) => {
      const aIndex = preferredOrder.indexOf(a.name ?? '');
      const bIndex = preferredOrder.indexOf(b.name ?? '');
      const left = aIndex === -1 ? preferredOrder.length + 1 : aIndex;
      const right = bIndex === -1 ? preferredOrder.length + 1 : bIndex;
      return left - right;
    })
    .slice(0, 8);

  el.innerHTML = `
    ${renderPageHero({
      label: t('technologies.label'),
      title: tech.title ?? t('technologies.title'),
      intro: tech.intro ?? t('technologies.intro'),
      kicker: 'Selection criteria',
      stack: ['Fast delivery', 'Longevity', 'Operational calm', 'Modern platform fit'],
    })}
    <section class="section">
      <div class="container tech-board-grid">
        ${activeCategories
          .map((category, index) => {
            const anchor = toAnchor(category.name ?? `category-${index + 1}`);
            return `
              <article class="tech-system-card animate-on-scroll" id="tech-${anchor}" data-route-anchor="${anchor}" style="animation-delay: ${(index % 3) * 0.05}s">
                <div class="tech-system-head">
                  <p class="tech-system-kicker">${escapeHtml(category.name ?? '')}</p>
                </div>
                <div class="tech-chip-grid">
                  ${(category.items ?? []).map((item) => renderTechItem(item.name, item.logoUrl)).join('')}
                </div>
              </article>
            `;
          })
          .join('')}
      </div>
    </section>
    ${renderPageCta({
      label: t('technologies.label'),
      title: t('technologies.cta.title'),
      body: t('technologies.cta.body'),
      buttonLabel: t('technologies.cta.button'),
    })}
  `;
}
